import React, { useEffect, useMemo, useState } from "react";
import "./MarkdownEditor.css";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  lineLength?: number; // 一行当たりの文字数（デフォルトは80）
  placeholder?: string;
  onInsertImage?: (keyName: string, insertPosition?: number) => void; // 画像挿入コールバック（カーソル位置を指定可能）
  availableImages?: Array<{
    keyName: string;
    name: string;
    previewUrl?: string;
    objectName?: string;
  }>; // 利用可能な画像一覧
  onImageInserted?: (newCursorPosition: number) => void; // 画像挿入後のカーソル位置を通知
  readOnly?: boolean; // 読み取り専用モード
  onInsertPlatformTag?: (platform: "ios" | "android" | "steam") => void; // プラットフォームタグ挿入コールバック
  previewPlatform?: "common" | "ios" | "android" | "steam"; // プレビュー時のプラットフォームフィルタ
  onPreviewPlatformChange?: (
    platform: "common" | "ios" | "android" | "steam"
  ) => void; // プレビュープラットフォーム変更コールバック
  filterContentByPlatform?: (
    content: string,
    platform: "common" | "ios" | "android" | "steam"
  ) => string; // プラットフォーム別コンテンツフィルタ関数
  enabledPlatforms?: { ios: boolean; android: boolean; steam: boolean }; // 有効なプラットフォーム
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  lineLength = 80,
  placeholder = "Markdown形式で入力してください...",
  onInsertImage,
  availableImages = [],
  onImageInserted,
  readOnly = false,
  onInsertPlatformTag,
  previewPlatform = "common",
  onPreviewPlatformChange,
  filterContentByPlatform,
  enabledPlatforms = { ios: true, android: true, steam: true },
}) => {
  const [previewMode, setPreviewMode] = useState<"edit" | "preview" | "split">(
    "split"
  );
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<
    Record<string, string>
  >({});
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // availableImagesのpreviewUrlを更新（objectNameがあるがpreviewUrlがない場合）
  useEffect(() => {
    const imagesNeedingPreview = availableImages.filter(
      (img) =>
        img.keyName &&
        img.objectName &&
        !img.previewUrl &&
        !imagePreviewUrls[img.keyName]
    );

    if (imagesNeedingPreview.length === 0) {
      return;
    }

    const newPreviewUrls: Record<string, string> = { ...imagePreviewUrls };

    for (const image of imagesNeedingPreview) {
      if (!image.objectName) continue;
      // Admin用画像プロキシURLを生成（署名不要）
      const previewUrl = `/api/admin/image/${image.objectName}`;
      newPreviewUrls[image.keyName] = previewUrl;
    }

    if (
      Object.keys(newPreviewUrls).length > Object.keys(imagePreviewUrls).length
    ) {
      setImagePreviewUrls(newPreviewUrls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableImages
      .map((img) => `${img.keyName}-${img.objectName}-${img.previewUrl || ""}`)
      .join(","),
  ]);

  // 画像挿入後のカーソル位置を設定
  useEffect(() => {
    if (onImageInserted && textareaRef.current) {
      const textarea = textareaRef.current;
      // 少し遅延させてからカーソル位置を設定（DOM更新を待つ）
      const timeoutId = setTimeout(() => {
        const newPosition = onImageInserted(0); // ダミー値、実際の位置は親から渡される
        if (newPosition >= 0) {
          textarea.selectionStart = textarea.selectionEnd = newPosition;
          textarea.focus();
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [value, onImageInserted]);

  // MarkdownをHTMLに変換（簡易実装）
  const markdownToHtml = useMemo(() => {
    return (markdown: string): string => {
      let html = markdown;

      // 見出し
      html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
      html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
      html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

      // 太字
      html = html.replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>");
      html = html.replace(/__(.*?)__/gim, "<strong>$1</strong>");

      // 斜体
      html = html.replace(/\*(.*?)\*/gim, "<em>$1</em>");
      html = html.replace(/_(.*?)_/gim, "<em>$1</em>");

      // コードブロック
      html = html.replace(/```([\s\S]*?)```/gim, "<pre><code>$1</code></pre>");
      html = html.replace(/`([^`]+)`/gim, "<code>$1</code>");

      // 画像（image:keyName形式を最優先で処理）
      html = html.replace(
        /!\[([^\]]*)\]\(image:([^)]+)\)/gim,
        (match, alt, keyName) => {
          // availableImagesから該当する画像を探す（keyNameまたはnameで検索）
          const image = availableImages.find(
            (img) => img.keyName === keyName || img.name === keyName
          );
          console.log(
            `[MarkdownEditor] 画像検索: keyName=${keyName}, availableImages=${availableImages.length}件`,
            availableImages
          );
          if (image) {
            console.log(
              `[MarkdownEditor] 画像見つかった: name=${image.name}, keyName=${
                image.keyName
              }, previewUrl=${
                image.previewUrl ? "あり" : "なし"
              }, imagePreviewUrls=${
                imagePreviewUrls[keyName] ? "あり" : "なし"
              }`
            );
            // previewUrlがある場合は使用（imagePreviewUrlsからも確認）
            const previewUrl =
              image.previewUrl ||
              imagePreviewUrls[keyName] ||
              imagePreviewUrls[image.keyName];
            if (previewUrl) {
              console.log(
                `[MarkdownEditor] 画像表示: ${previewUrl.substring(0, 50)}...`
              );
              return `<img src="${previewUrl}" alt="${
                alt || keyName
              }" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`;
            }
            // previewUrlがないがobjectNameがある場合は、後でuseEffectで生成される
            // ここではプレースホルダーを表示
            console.log(
              `[MarkdownEditor] プレビューURLなし: keyName=${keyName}`
            );
            return `<div style="padding: 20px; border: 1px dashed #ccc; text-align: center; color: #999;">画像: ${keyName} (読み込み中...)</div>`;
          }
          // 画像が見つからない場合はプレースホルダーを表示
          console.log(`[MarkdownEditor] 画像見つからない: keyName=${keyName}`);
          return `<div style="padding: 20px; border: 1px dashed #ccc; text-align: center; color: #999;">画像: ${keyName} (見つかりません)</div>`;
        }
      );

      // 動画（video:keyName形式を処理）
      html = html.replace(
        /!\[([^\]]*)\]\(video:([^)]+)\)/gim,
        (match, alt, keyName) => {
          // availableImagesから該当する動画を探す（keyNameまたはnameで検索）
          const video = availableImages.find(
            (img) => img.keyName === keyName || img.name === keyName
          );
          if (video) {
            const previewUrl =
              video.previewUrl ||
              imagePreviewUrls[keyName] ||
              imagePreviewUrls[video.keyName];
            if (previewUrl) {
              return `<video src="${previewUrl}" controls style="max-width: 100%; height: auto; display: block; margin: 10px 0;"></video>`;
            }
            return `<div style="padding: 20px; border: 1px dashed #ccc; text-align: center; color: #999;">動画: ${keyName} (読み込み中...)</div>`;
          }
          return `<div style="padding: 20px; border: 1px dashed #ccc; text-align: center; color: #999;">動画: ${keyName} (見つかりません)</div>`;
        }
      );

      // 通常の画像URL（image:やvideo:で始まらないもののみ）
      html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, (match, alt, url) => {
        // image:やvideo:で始まる場合はスキップ（既に処理済み）
        if (url.startsWith("image:") || url.startsWith("video:")) {
          return match;
        }
        return `<img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;" />`;
      });

      // リンク（画像以外）
      html = html.replace(
        /\[([^\]]+)\]\(([^)]+)\)/gim,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      );

      // リスト
      html = html.replace(/^\* (.*$)/gim, "<li>$1</li>");
      html = html.replace(/^- (.*$)/gim, "<li>$1</li>");
      html = html.replace(/^\+ (.*$)/gim, "<li>$1</li>");
      html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

      // 番号付きリスト
      html = html.replace(/^\d+\. (.*$)/gim, "<li>$1</li>");
      html = html.replace(/(<li>.*<\/li>)/s, "<ol>$1</ol>");

      // 改行
      html = html.replace(/\n/gim, "<br />");

      return html;
    };
  }, [
    availableImages
      .map(
        (img) =>
          `${img.keyName}-${img.previewUrl || ""}-${img.objectName || ""}`
      )
      .join(","),
    JSON.stringify(imagePreviewUrls),
  ]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    // カーソル位置を保存
    setCursorPosition(e.target.selectionStart);
  };

  // カーソル位置を更新
  const handleTextareaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    setCursorPosition(textarea.selectionStart);
  };

  const handleTextareaKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = e.currentTarget;
    setCursorPosition(textarea.selectionStart);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tabキーでインデント
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + "  " + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };

  const handleInsertImageClick = (keyName: string) => {
    if (onInsertImage) {
      // カーソル位置を取得
      const textarea = textareaRef.current;
      const position = textarea ? textarea.selectionStart : cursorPosition;
      onInsertImage(keyName, position);

      // フォーカスを戻す
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
        }
      }, 0);
    }
  };

  const handleInsertPlatformTagClick = (
    platform: "ios" | "android" | "steam"
  ) => {
    if (onInsertPlatformTag) {
      onInsertPlatformTag(platform);

      // フォーカスを戻す
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
        }
      }, 0);
    }
  };

  return (
    <div className="markdown-editor">
      <div className="markdown-editor-toolbar">
        <button
          className={`toolbar-btn ${previewMode === "edit" ? "active" : ""}`}
          onClick={() => setPreviewMode("edit")}
        >
          編集
        </button>
        <button
          className={`toolbar-btn ${previewMode === "split" ? "active" : ""}`}
          onClick={() => setPreviewMode("split")}
        >
          分割
        </button>
        <button
          className={`toolbar-btn ${previewMode === "preview" ? "active" : ""}`}
          onClick={() => setPreviewMode("preview")}
        >
          プレビュー
        </button>
        {onInsertPlatformTag && !readOnly && (
          <>
            {enabledPlatforms.ios && (
              <button
                className="toolbar-btn"
                onClick={() => handleInsertPlatformTagClick("ios")}
                title="iOS専用タグを挿入"
              >
                🍎 iOSタグ
              </button>
            )}
            {enabledPlatforms.android && (
              <button
                className="toolbar-btn"
                onClick={() => handleInsertPlatformTagClick("android")}
                title="Android専用タグを挿入"
              >
                🤖 Androidタグ
              </button>
            )}
            {enabledPlatforms.steam && (
              <button
                className="toolbar-btn"
                onClick={() => handleInsertPlatformTagClick("steam")}
                title="Steam専用タグを挿入"
              >
                🎮 Steamタグ
              </button>
            )}
          </>
        )}
        {availableImages.length > 0 && !readOnly && (
          <div className="toolbar-image-buttons">
            {availableImages.map((img) => (
              <button
                key={img.keyName}
                className="toolbar-btn toolbar-image-btn"
                onClick={() => handleInsertImageClick(img.keyName)}
                title={`${img.name} (${img.keyName})を挿入`}
              >
                📷 {img.keyName}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="markdown-editor-content">
        {(previewMode === "edit" || previewMode === "split") && (
          <div
            className={`markdown-editor-input ${
              previewMode === "split" ? "split" : "full"
            }`}
          >
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onClick={handleTextareaClick}
              onKeyUp={handleTextareaKeyUp}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={readOnly}
              className="markdown-textarea"
              style={{
                fontFamily: "monospace",
                fontSize: "14px",
                lineHeight: "1.6",
                width: "100%",
                height: "100%",
                padding: "16px",
                border: "none",
                outline: "none",
                resize: "none",
                boxSizing: "border-box",
                ...(readOnly
                  ? { backgroundColor: "#f3f4f6", cursor: "not-allowed" }
                  : {}),
              }}
            />
            <div className="markdown-editor-line-indicator">
              {Array.from({ length: Math.ceil(value.length / lineLength) }).map(
                (_, i) => (
                  <div key={i} className="line-indicator" />
                )
              )}
            </div>
          </div>
        )}

        {(previewMode === "preview" || previewMode === "split") && (
          <div
            className={`markdown-editor-preview ${
              previewMode === "split" ? "split" : "full"
            }`}
          >
            {onPreviewPlatformChange && filterContentByPlatform && (
              <div className="markdown-preview-platform-selector">
                <div className="platform-selector-label">表示:</div>
                <div className="platform-selector-buttons">
                  <button
                    type="button"
                    className={`platform-selector-btn ${
                      previewPlatform === "common" ? "active" : ""
                    }`}
                    onClick={() => onPreviewPlatformChange("common")}
                    title="共通コンテンツを表示"
                  >
                    <span className="platform-icon">📄</span>
                    <span className="platform-label">共通</span>
                  </button>
                  {enabledPlatforms.ios && (
                    <button
                      type="button"
                      className={`platform-selector-btn ${
                        previewPlatform === "ios" ? "active" : ""
                      }`}
                      onClick={() => onPreviewPlatformChange("ios")}
                      title="iOSアプリでの表示"
                    >
                      <span className="platform-icon">🍎</span>
                      <span className="platform-label">iOS</span>
                    </button>
                  )}
                  {enabledPlatforms.android && (
                    <button
                      type="button"
                      className={`platform-selector-btn ${
                        previewPlatform === "android" ? "active" : ""
                      }`}
                      onClick={() => onPreviewPlatformChange("android")}
                      title="Androidアプリでの表示"
                    >
                      <span className="platform-icon">🤖</span>
                      <span className="platform-label">Android</span>
                    </button>
                  )}
                  {enabledPlatforms.steam && (
                    <button
                      type="button"
                      className={`platform-selector-btn ${
                        previewPlatform === "steam" ? "active" : ""
                      }`}
                      onClick={() => onPreviewPlatformChange("steam")}
                      title="Steamでの表示"
                    >
                      <span className="platform-icon">🎮</span>
                      <span className="platform-label">Steam</span>
                    </button>
                  )}
                </div>
              </div>
            )}
            <div
              className="markdown-preview-content"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(
                  filterContentByPlatform && previewPlatform
                    ? filterContentByPlatform(value, previewPlatform)
                    : value
                ),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
